import { handleActions } from 'redux-actions';
import http from '../shared/http';
import intl from '../shared/intl';
import endpoints from '../../config/endpoints';
import { create as paginationCreate } from '../shared/pagination';
import { LOCATION_CHANGE, replace } from 'react-router-redux';
import { SubmissionError } from 'redux-form';
import { baseUrl } from '../../config/route-entry-point';
import { REFETCH_ALL_DATA } from '../root';

import capitalize from 'lodash/capitalize';
import cloneDeep from 'lodash/cloneDeep';
import values from 'lodash/values';
import isObject from 'lodash/isObject';
import orderBy from 'lodash/orderBy';
import find from 'lodash/find';
import map from 'lodash/map';
import forEach from 'lodash/forEach';

const pagination = paginationCreate('spaces', {
	SpacesTable: {
		currentPage: 1,
		perPage: 10
	}
});

const FETCH_SPACES_SUCCESS = 'spaces/fetch-data-success';
const FETCH_SPACES_ERROR = 'spaces/fetch-data-error';

const SPACES_DELETE_ITEM_SUCCESS = 'spaces/delete-item-success';
const SPACES_DELETE_ITEM_ERROR = 'spaces/delete-item-error';

const SORT_BY_COLUMN = 'spaces/sort-by-column';

const SAVE_FORM_DATA_SUCCESS = 'spaces/save-form-data-success';
const SAVE_FORM_DATA_ERROR = 'spaces/save-form-data-error';

const SPACES_SHOW_PROGRESS_BAR = 'spaces/show-progress-bar';

const initialState = {
	data: null,
	error: null,
	pagination: pagination.initState,
	columns: {
		'title': {
			id: 'title',
			direction: 'desc',
			position: 'title',
			title: 'Название'
		},
		'type': {
			id: 'type',
			direction: 'desc',
			position: 'type',
			title: 'Тип'
		},
		'action': {
			id: 'action',
			title: 'Действия'
		}
	},
	columnSorting: {
		key: 'updated_at',
		direction: 'desc',
		position: 'updated_at',
		title: 'Название'
	},
	types: [],
	progressBar: false
};

export const actions = {
	fetchData: () => (dispatch, getState) => {
		const types = map(getState().app.lists['space-types'], it => {
			let type = Object.assign(it, {
				text: capitalize(it.text),
				className: `type-${it.id}`,
				active: false,
				title: null
			});
			return type;
		});

		(http({
			url: [
				`${endpoints.SPACES.LIST}`
			],
			method: 'get',
			actions: [
				FETCH_SPACES_SUCCESS,
				FETCH_SPACES_ERROR
			],
			addToResponse: types
		}))(dispatch);
	},

	pagination: () => dispatch => pagination.actions(dispatch),

	deleteItem: itemId => dispatch => {
		(http({
			url: [
				`${endpoints.SPACES.LIST}/${itemId}`
			],
			method: 'delete',
			actions: [
				SPACES_DELETE_ITEM_SUCCESS,
				SPACES_DELETE_ITEM_ERROR
			]
		}))(dispatch);
	},

	sortByColumn: columnKey => ({
		type: SORT_BY_COLUMN,
		payload: columnKey
	}),

	formSaveData: (formData) => (dispatch, getState) => {
		dispatch({type: SPACES_SHOW_PROGRESS_BAR});

		const itemId = formData.itemId;
		const request = http({
			url: [
				`${endpoints.SPACES.LIST + (itemId ? '/' + itemId : '')}`
			],
			method: itemId ? 'put' : 'post',
			actions: [
				SAVE_FORM_DATA_SUCCESS,
				SAVE_FORM_DATA_ERROR
			],
			data: {
				type: formData.id,
				title: formData.title
			}
		});

		return request(dispatch).then(action => {
			const { type, payload } = action;
			if (type === SAVE_FORM_DATA_SUCCESS) {
				dispatch(replace(baseUrl('spaces/')));
			}

			if (action.type === SAVE_FORM_DATA_ERROR && payload.response.status === 400) {
				const validationErrors = {};
				const responseErrors = payload.response.data;

				forEach(responseErrors, value => {
					let key = value.dataPath.slice(1);
					validationErrors[key] = value.message;
				});

				throw new SubmissionError(validationErrors);
			}
		});
	},

	cancelForm: () => dispatch => {
		dispatch(replace(baseUrl('spaces/')));
	}
};

const reducers = Object.assign({
	[LOCATION_CHANGE]: (state, action) => {
		if (action.payload.pathname.indexOf('spaces/') !== -1) {
			return initialState;
		}
		return state;
	},
	[FETCH_SPACES_SUCCESS]: (state, action) => {
		const [ data ] = action.payload.map(res => res.data);
		return { ...state, data: data, types: action.meta };
	},
	[FETCH_SPACES_ERROR]: (state, action) => {
		return { ...state, error: true };
	},
	[SPACES_DELETE_ITEM_SUCCESS]: state => {
		return { ...state, data: null };
	},
	[SPACES_DELETE_ITEM_ERROR]: state => {
		return { ...state, error: true };
	},
	[SORT_BY_COLUMN]: (state, action) => {
		const { columnSorting, columns } = state;
		const columnKey = action.payload;

		const columnDirection = columnSorting && columnSorting['key'] === columnKey
			? columnSorting.direction === 'desc' ? 'asc' : 'desc'
			: columns[action.payload].direction;

		return {
			...state,
			columnSorting: {
				...columns[columnKey],
				key: action.payload,
				direction: columnDirection
			}
		};
	},
	[SPACES_SHOW_PROGRESS_BAR]: state => {
		return { ...state, progressBar: true };
	},
	[SAVE_FORM_DATA_SUCCESS]: (state, action) => {
		return { ...initialState };
	},
	[REFETCH_ALL_DATA]: () => initialState
}, pagination.reducers);

export default handleActions(reducers, cloneDeep(initialState));

export const selectors = {
	getData: state => state.spaces.data,
	getTableHeaders: state => values(state.spaces.columns),
	getColumnSorting: state => state.spaces.columnSorting,
	getTableData: state => pagination.selectors.getPageData(
		state => state.spaces.data && sortByColumn(state.spaces.columnSorting, state.spaces.data),
		state => state.spaces.pagination.SpacesTable.perPage,
		state => state.spaces.pagination.SpacesTable.currentPage
	)(state),
	getTypes: state => state.spaces.types,
	getForm: state => ({
		options: state.app.lists,
		dataByParams: (typeId, itemId = null) => {
			const activeType = find(state.spaces.types, type => type.id === typeId);
			const data = (find(state.spaces.data, item => item.id === parseInt(itemId))) || {};

			if (!activeType) return null;

			activeType.title = data.title || null;

			return activeType;
		}
	}),
	getIntl: state => intl(state),
	getError: state => state.spaces.error
};

const sortByColumn = (sortByColumn, rows) => {
	if (!rows) return null;

	return orderBy(rows, row => {
		const value = row[sortByColumn.position];

		if (isObject(value)) {
			return value.text;
		}

		return value;

	}, sortByColumn.direction);
};



// WEBPACK FOOTER //
// ./frontend/cabinets/modules/spaces/index.js